import type { ChangeEvent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { SingleValue } from 'react-select';
import { Input } from '@/app/components/form/Input';
import { Selector } from '@/app/components/form/Selector';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import { SingleDatePicker } from '@/app/components/form/SingleDatePicker';
import { Toggle } from '@/app/components/form/Toggle';
import { useCountrySelector } from '@/hooks/useFormCountryList/useFormCountryList';
import type { DocumentType } from '@/types/documentsApiTypes';
import type { PassportFormData } from './types';

export const PassportForm = () => {
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<PassportFormData>();

  const { countryOptions } = useCountrySelector({
    selectedId: watch('issuance_country'),
    url: '/api/worldwide/countries',
  });
  const documentType = watch('document_type');

  const handleDocumentNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue('document_name', e.target.value);
  };

  const handlePassportNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue('passport_number', e.target.value);
  };

  const handlePassportIssueCountryChange = (value: SingleValue<SelectorOption<string>>) => {
    setValue('issuance_country', value?.value ?? '');
  };

  const handlePassportIssueDateChange = (val: string) => {
    setValue('issue_date', val);
  };

  const handlePassportExpiryDateChange = (val: string) => {
    setValue('expiry_date', val);
  };

  const handleDocumentTypeChange = (val: DocumentType) => {
    setValue('document_type', val);
  };

  return (
    <div className="flex flex-col gap-4 border-b border-grey-400 py-5 max-md:py-4">
      <div className="flex flex-col">
        <Controller
          name="document_type"
          control={control}
          render={({ field }) => (
            <Toggle
              checked={field.value === 'passport'}
              onChange={() => handleDocumentTypeChange(field.value === 'passport' ? 'other' : 'passport')}
              label="This is a passport"
            />
          )}
        />
      </div>
      {documentType === 'other' || documentType === 'pdf_uploads' ? (
        <Input
          {...register('document_name')}
          intent="primaryWhite"
          placeholder="Enter document type"
          label="Document name"
          errors={errors}
          onChange={handleDocumentNameChange}
        />
      ) : (
        <div className="grid grid-cols-2 gap-y-6 gap-x-5 w-full max-md:grid-cols-1 max-md:gap-4">
          <Input
            {...register('passport_number')}
            onChange={handlePassportNumberChange}
            label="Passport number"
            value={watch('passport_number')}
            placeholder="Enter passport number"
            errors={errors}
          />
          <Selector
            options={countryOptions}
            placeholder="Select issue country"
            className="text-left"
            {...register('issuance_country')}
            onChange={handlePassportIssueCountryChange}
            label="Passport issuing country"
            value={watch('issuance_country')}
          />
          <SingleDatePicker
            label="Date of Issue"
            name="issue_date"
            value={watch('issue_date')}
            onChangeDate={handlePassportIssueDateChange}
          />
          <SingleDatePicker
            label="Date of Expiration"
            name="expiry_date"
            value={watch('expiry_date')}
            onChangeDate={handlePassportExpiryDateChange}
          />
        </div>
      )}
    </div>
  );
};
